import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Space,
  message,
  Row,
  Col,
  Upload,
  Card,
  Divider,
  Switch,
  Empty,
  Popconfirm,
  Tag,
  Table,
  Typography,
  Alert,
} from "antd";
import {
  UploadOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { WorkLineSource, WorkRequestLine } from "../types";
import { getMasterData } from "../mockData/workRequestData";
import { nanoid } from "nanoid";

const { Text } = Typography;

const { Option } = Select;
const { TextArea } = Input;

interface NewWorkLineCreatorProps {
  onLineCreated: (line: WorkRequestLine) => void;
  editingLine?: WorkRequestLine | null;
}

const NewWorkLineCreator: React.FC<NewWorkLineCreatorProps> = ({
  onLineCreated,
  editingLine,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [subCategories, setSubCategories] = useState<any[]>([]);
  const [uomList, setUomList] = useState<any[]>([]);
  const [fileList, setFileList] = useState<any[]>([]);
  
  // Material BOQ states
  const [includeMaterialBOQ, setIncludeMaterialBOQ] = useState<boolean>(false);
  const [materialOptions, setMaterialOptions] = useState<any[]>([]);
  const [allocatedMaterials, setAllocatedMaterials] = useState<any[]>([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>("");
  const [materialQuantity, setMaterialQuantity] = useState<number>(1);
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<number>(0);
  useEffect(() => {
    fetchMasterData();
    fetchMaterialOptions();
  }, []);

  useEffect(() => {
    // Initialize form if editing
    if (editingLine) {
      const masterData = getMasterData();
      const category = masterData.categories.find(
        (cat) => cat.name === editingLine.category
      );
      const subCategory = masterData.categories.find(
        (cat) => cat.name === editingLine.subcategory
      );
      const uom = masterData.uomList.find((u) => u.code === editingLine.uom);

      form.setFieldsValue({
        workCategory: category?.id,
        workSubCategory: subCategory?.id,
        taskTitle: editingLine.taskTitle,
        taskDescription: editingLine.taskDescription,
        quantity: editingLine.quantity,
        uom: uom?.id,
        assignedTower: editingLine.assignedTower,
        assignedVilla: editingLine.assignedVilla,
      });

      // Set material allocation data if exists
      if ((editingLine as any).materialAllocation) {
        setAllocatedMaterials((editingLine as any).materialAllocation);
        setIncludeMaterialBOQ(true);
      }

      // Set subcategories for selected category
      if (category) {
        handleCategoryChange(category.id);
      }
    }
  }, [editingLine, form]);

  const fetchMasterData = () => {
    const masterData = getMasterData();
    setCategories(masterData.categories.filter((cat) => !cat.parentId)); // Only parent categories
    setUomList(masterData.uomList);
  };

  const fetchMaterialOptions = () => {
    // Mock material options - in real implementation, this would come from Material BOQ API
    const mockMaterials = [
      {
        label: "Steel Bars 12mm",
        value: "steel_bars_12mm",
        uom: "kg",
        category: "Steel",
        description: "High grade steel bars",
      },
      {
        label: "Cement Bags (50kg)",
        value: "cement_bags",
        uom: "bags",
        category: "Cement",
        description: "Premium cement bags",
      },
      {
        label: "Sand (River)",
        value: "sand_river",
        uom: "cum",
        category: "Aggregates",
        description: "Fine river sand",
      },
      {
        label: "Electrical Wire 2.5mm",
        value: "wire_2_5mm",
        uom: "m",
        category: "Electrical",
        description: "Copper electrical wire",
      },
      {
        label: "PVC Pipes 4 inch",
        value: "pvc_pipes_4_inch",
        uom: "m",
        category: "Plumbing",
        description: "PVC pipes for plumbing",
      },
      {
        label: "Paint (Interior) - White",
        value: "paint_interior_white",
        uom: "ltrs",
        category: "Paint",
        description: "Interior wall paint",
      },
    ];
    setMaterialOptions(mockMaterials);
  };

  const handleCategoryChange = (categoryId: string) => {
    // Find subcategories for selected category
    const masterData = getMasterData();
    const subcats = masterData.categories.filter(
      (cat) => cat.parentId === categoryId
    );
    setSubCategories(subcats);

    // Clear subcategory when category changes (except during initialization)
    if (!editingLine) {
      form.setFieldValue("workSubCategory", undefined);
    }
  };

  // Material allocation methods
  const getAvailableMaterials = () => {
    const allocatedIds = allocatedMaterials.map(
      (material) => material.materialId
    );
    return materialOptions.filter(
      (option) => !allocatedIds.includes(option.value)
    );
  };

  const handleAddMaterial = () => {
    if (!selectedMaterialId) {
      message.warning("Please select a material first");
      return;
    }

    if (!materialQuantity || materialQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    if (
      allocatedMaterials.some(
        (material) => material.materialId === selectedMaterialId
      )
    ) {
      message.error("This material has already been allocated");
      return;
    }

    const materialToAdd = materialOptions.find(
      (material) => material.value === selectedMaterialId
    );

    if (!materialToAdd) {
      message.error("Selected material not found");
      return;
    }

    const newAllocatedMaterial = {
      id: nanoid(),
      materialId: materialToAdd.value,
      materialName: materialToAdd.label,
      quantity: materialQuantity,
      uom: materialToAdd.uom || "nos",
      category: materialToAdd.category,
    };

    const updatedMaterials = [...allocatedMaterials, newAllocatedMaterial];
    setAllocatedMaterials(updatedMaterials);

    setSelectedMaterialId("");
    setMaterialQuantity(1);

    message.success(`${materialToAdd.label} added successfully`);
  };

  const handleRemoveMaterial = (materialId: string) => {
    const materialToRemove = allocatedMaterials.find(
      (m) => m.id === materialId
    );
    const updatedMaterials = allocatedMaterials.filter(
      (material) => material.id !== materialId
    );

    setAllocatedMaterials(updatedMaterials);
    message.success(`${materialToRemove?.materialName} removed successfully`);
  };

  const handleStartEdit = (materialId: string, currentQuantity: number) => {
    setEditingMaterialId(materialId);
    setEditingQuantity(currentQuantity);
  };

  const handleSaveEdit = (materialId: string) => {
    if (editingQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    const updatedMaterials = allocatedMaterials.map((material) =>
      material.id === materialId
        ? { ...material, quantity: editingQuantity }
        : material
    );

    setAllocatedMaterials(updatedMaterials);
    setEditingMaterialId(null);
    message.success("Quantity updated successfully");
  };

  const handleCancelEdit = () => {
    setEditingMaterialId(null);
    setEditingQuantity(0);
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);

      // Find category and subcategory names
      const selectedCategory = categories.find(
        (cat) => cat.id === values.workCategory
      );
      const selectedSubCategory = subCategories.find(
        (cat) => cat.id === values.workSubCategory
      );
      const selectedUOM = uomList.find((uom) => uom.id === values.uom);

      // Create work request line
      const workRequestLine: WorkRequestLine = {
        id: editingLine?.id || `${Date.now()}-${Math.random()}`,
        taskTitle: values.taskTitle.trim(),
        taskDescription: values.taskDescription?.trim(),
        category: selectedCategory?.name || values.workCategory,
        subcategory: selectedSubCategory?.name,
        quantity: values.quantity,
        uom: selectedUOM?.code || values.uom,
        source: WorkLineSource.NewWorkLine,
        assignedTower: values.assignedTower,
        assignedVilla: values.assignedVilla,
        documents: fileList.map((file) => file.name) || [],
        materialAllocation: includeMaterialBOQ ? allocatedMaterials : [],
      };

      onLineCreated(workRequestLine);

      if (!editingLine) {
        form.resetFields();
        setFileList([]);
        setSubCategories([]);
        setAllocatedMaterials([]);
        setIncludeMaterialBOQ(false);
        setSelectedMaterialId("");
        setMaterialQuantity(1);
        message.success("Work line created successfully");
      } else {
        message.success("Work line updated successfully");
      }
    } catch (error) {
      console.log("Error creating work line:", error);
      message.error("Failed to create work line");
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    fileList,
    onChange: ({ fileList: newFileList }: any) => setFileList(newFileList),
    beforeUpload: () => false, // Prevent auto upload
    multiple: true,
  };

  // Tower options
  const towerOptions = ["Tower A", "Tower B", "Tower C", "Tower D"];

  // Villa options
  const villaOptions = ["Villa 1", "Villa 2", "Villa 3", "Villa 4", "Villa 5"];

  return (
    <div>
      {editingLine && (
        <div className="alert alert-info mb-3">
          <strong>Edit Mode:</strong> Editing {editingLine.taskTitle} - Only
          quantity and location can be modified
        </div>
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          quantity: 1,
        }}
      >
        <Card title="Work Line Details" className="mb-3">
          <Row gutter={16}>
            {/* Work Category - Mandatory (FSD Field 1) */}
            <Col span={12}>
              <Form.Item
                name="workCategory"
                label="Work Category"
                rules={[
                  { required: true, message: "Work Category is required" },
                ]}
              >
                <Select
                  placeholder="Work type category"
                  onChange={handleCategoryChange}
                  disabled={editingLine ? true : false}
                >
                  {categories.map((category) => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                      {category.description && (
                        <span className="text-muted">
                          {" "}
                          - {category.description}
                        </span>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Work Sub Category - Mandatory (FSD Field 2) */}
            <Col span={12}>
              <Form.Item
                name="workSubCategory"
                label="Work Sub Category"
                rules={[
                  { required: true, message: "Work Sub Category is required" },
                ]}
              >
                <Select
                  placeholder="Work subcategory"
                  disabled={editingLine ? true : subCategories.length === 0}
                >
                  {subCategories.map((subCategory) => (
                    <Option key={subCategory.id} value={subCategory.id}>
                      {subCategory.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Task Title - Mandatory (FSD Field 3) */}
            <Col span={24}>
              <Form.Item
                name="taskTitle"
                label="Task Title"
                rules={[
                  { required: true, message: "Task Title is required" },
                  {
                    min: 3,
                    message: "Task title must be at least 3 characters",
                  },
                  {
                    max: 200,
                    message: "Task title must be less than 200 characters",
                  },
                ]}
              >
                <Input
                  placeholder="Task heading"
                  disabled={editingLine ? true : false}
                />
              </Form.Item>
            </Col>

            {/* Task Description - Optional (FSD Field 4) */}
            <Col span={24}>
              <Form.Item name="taskDescription" label="Task Description">
                <TextArea
                  placeholder="Description of task"
                  rows={3}
                  maxLength={500}
                  showCount
                  disabled={editingLine ? true : false}
                />
              </Form.Item>
            </Col>

            {/* Quantity - Mandatory (FSD Field 5) */}
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[
                  { required: true, message: "Quantity is required" },
                  {
                    type: "number",
                    min: 0.01,
                    message: "Quantity must be greater than 0",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Required work quantity"
                  style={{ width: "100%" }}
                  min={0.01}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>

            {/* UOM - Mandatory (FSD Field 6) */}
            <Col span={12}>
              <Form.Item
                name="uom"
                label="UOM"
                rules={[
                  {
                    required: true,
                    message: "Unit of Measurement is required",
                  },
                ]}
              >
                <Select
                  placeholder="Unit of Measurement"
                  disabled={editingLine ? true : false}
                >
                  {uomList.map((uom) => (
                    <Option key={uom.id} value={uom.id}>
                      {uom.name} ({uom.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Location & Assignment" className="mb-3">
          <Row gutter={16}>
            {/* Tower / Villa Select - Conditional (FSD Field 8) */}
            <Col span={12}>
              <Form.Item name="assignedTower" label="Tower Select">
                <Select placeholder="Select Tower" allowClear>
                  {towerOptions.map((tower) => (
                    <Option key={tower} value={tower}>
                      {tower}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="assignedVilla" label="Villa Select">
                <Select placeholder="Select Villa" allowClear>
                  {villaOptions.map((villa) => (
                    <Option key={villa} value={villa}>
                      {villa}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Floor / Flat Details - Conditional (FSD Field 9) */}
            <Col span={24}>
              <Form.Item name="floorFlatDetails" label="Floor / Flat Details">
                <Input
                  placeholder="Further filters based on tower/villa selection"
                  disabled={editingLine ? true : false}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Material BOQ Section */}
        <Card title="Material Allocation" className="mb-3">
          <Row gutter={16}>
            <Col span={24}>
              <div className="mb-3">
                <label className="form-label d-flex align-items-center mb-2">
                  Include Material BOQ
                  <Switch
                    checked={includeMaterialBOQ}
                    onChange={(checked) => {
                      setIncludeMaterialBOQ(checked);
                      if (!checked) {
                        setAllocatedMaterials([]);
                        setSelectedMaterialId("");
                        setMaterialQuantity(1);
                      }
                    }}
                    className="ms-2"
                    disabled={editingLine ? true : false}
                  />
                </label>
                <div className="form-note text-muted">
                  Enable to allocate materials from Material BOQ to this work
                  line
                </div>
              </div>

              {includeMaterialBOQ && (
                <div>
                  {/* Add Material Section */}
                  <Card
                    size="small"
                    title="Add Material"
                    style={{ marginBottom: "16px" }}
                    bodyStyle={{ padding: "16px" }}
                  >
                    <Row gutter={16} align="bottom">
                      <Col xs={24} sm={10} md={10}>
                        <label className="form-label mb-1">
                          Select Material
                        </label>
                        <Select
                          placeholder="Choose material to allocate"
                          value={selectedMaterialId || undefined}
                          onChange={setSelectedMaterialId}
                          showSearch
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase()) || false
                          }
                          disabled={getAvailableMaterials().length === 0}
                          style={{ width: "100%" }}
                        >
                          {getAvailableMaterials().map((material) => (
                            <Option key={material.value} value={material.value}>
                              <div>
                                <Text>{material.label}</Text>
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: 8, fontSize: "12px" }}
                                >
                                  ({material.uom})
                                </Text>
                                {material.category && (
                                  <Tag color="blue" style={{ marginLeft: 8 }}>
                                    {material.category}
                                  </Tag>
                                )}
                              </div>
                            </Option>
                          ))}
                        </Select>
                      </Col>

                      <Col xs={12} sm={6} md={6}>
                        <label className="form-label mb-1">Quantity</label>
                        <InputNumber
                          min={0.01}
                          step={0.01}
                          value={materialQuantity}
                          onChange={(value) => setMaterialQuantity(value || 1)}
                          placeholder="Enter qty"
                          style={{ width: "100%" }}
                        />
                      </Col>

                      <Col xs={12} sm={4} md={4}>
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleAddMaterial}
                          disabled={
                            !selectedMaterialId ||
                            !materialQuantity ||
                            materialQuantity <= 0
                          }
                          style={{ width: "100%", marginTop: "32px" }}
                        >
                          Add
                        </Button>
                      </Col>
                    </Row>

                    {getAvailableMaterials().length === 0 && (
                      <Alert
                        message="No materials available"
                        description="All available materials have been allocated."
                        type="info"
                        showIcon
                        style={{ marginTop: 16 }}
                      />
                    )}
                  </Card>

                  {/* Allocated Materials Table */}
                  <Card
                    size="small"
                    title={`Allocated Materials (${allocatedMaterials.length})`}
                  >
                    {allocatedMaterials.length > 0 ? (
                      <Table
                        columns={[
                          {
                            title: "Material Name",
                            dataIndex: "materialName",
                            key: "materialName",
                            width: "40%",
                            render: (name: string, record: any) => (
                              <div>
                                <Text strong>{name}</Text>
                                {record.category && (
                                  <div style={{ marginTop: 4 }}>
                                    <Tag color="blue">{record.category}</Tag>
                                  </div>
                                )}
                              </div>
                            ),
                          },
                          {
                            title: "Quantity",
                            dataIndex: "quantity",
                            key: "quantity",
                            width: "25%",
                            render: (quantity: number, record: any) => {
                              const isEditing = editingMaterialId === record.id;

                              return isEditing ? (
                                <Space>
                                  <InputNumber
                                    min={0.01}
                                    step={0.01}
                                    value={editingQuantity}
                                    onChange={(value) =>
                                      setEditingQuantity(value || 0)
                                    }
                                    style={{ width: "80px" }}
                                    size="small"
                                  />
                                  <Button
                                    type="text"
                                    icon={<CheckOutlined />}
                                    onClick={() => handleSaveEdit(record.id)}
                                    size="small"
                                    style={{ color: "#52c41a" }}
                                  />
                                  <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelEdit}
                                    size="small"
                                    style={{ color: "#ff4d4f" }}
                                  />
                                </Space>
                              ) : (
                                <Space>
                                  <Text>{quantity}</Text>
                                  <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      handleStartEdit(record.id, quantity)
                                    }
                                    size="small"
                                  />
                                </Space>
                              );
                            },
                          },
                          {
                            title: "UOM",
                            dataIndex: "uom",
                            key: "uom",
                            width: "15%",
                            render: (uom: string) => (
                              <Tag color="geekblue">{uom}</Tag>
                            ),
                          },
                          {
                            title: "Actions",
                            key: "actions",
                            width: "20%",
                            render: (_, record: any) => (
                              <Popconfirm
                                title="Remove Material"
                                description={`Are you sure you want to remove ${record.materialName}?`}
                                onConfirm={() =>
                                  handleRemoveMaterial(record.id)
                                }
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  size="small"
                                >
                                  Remove
                                </Button>
                              </Popconfirm>
                            ),
                          },
                        ]}
                        dataSource={allocatedMaterials}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        scroll={{ x: 600 }}
                      />
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                          <span>
                            No materials allocated yet
                            <br />
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              Use the form above to add materials
                            </Text>
                          </span>
                        }
                        style={{ padding: "40px 20px" }}
                      />
                    )}
                  </Card>
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {!editingLine && (
          <Card title="Additional Information" className="mb-3">
            <Row gutter={16}>
              {/* Allocate Material - Section Grid (FSD Field 7) */}
              <Col span={24}>
                <Form.Item name="allocateMaterial" label="Allocate Material">
                  <div className="p-3 border rounded bg-light">
                    <p className="text-muted mb-0">
                      <strong>Material Allocation:</strong> Allocated materials
                      from Material BOQ if applicable. This section will be
                      implemented with material linking functionality.
                    </p>
                  </div>
                </Form.Item>
              </Col>

              {/* Upload Documents - Optional (FSD Field 10) */}
              <Col span={24}>
                <Form.Item name="documents" label="Upload Documents">
                  <Upload {...uploadProps}>
                    <Button icon={<UploadOutlined />}>
                      Attach supporting documents
                    </Button>
                  </Upload>
                  {fileList.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        {fileList.length} file(s) selected
                      </small>
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        {/* Submit Button */}
        <div className="d-flex justify-content-end">
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<CheckOutlined />}
            >
              {editingLine ? "Update Work Line" : "Create Work Line"}
            </Button>
          </Space>
        </div>
      </Form>

      {/* FSD Note */}
      {/* <div className="mt-3 p-2 bg-light rounded">
        <small className="text-muted">
          <strong>FSD Reference:</strong> New Work Line Creation (Similar to Work BOQ Lines).
          📌 Users can add/edit/remove work lines dynamically.
        </small>
      </div> */}
    </div>
  );
};

export default NewWorkLineCreator;
